import React, { useState, useEffect, useRef } from 'react';
import {
  Button,
  Box,
  Autocomplete,
  Checkbox,
  CircularProgress,
  Container,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  Icon,
  IconButton,
  InputAdornment,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Stack,
  Select,
  SvgIcon,
  TextField,
  Typography,
  RadioGroup, Radio,
  styled
} from '@mui/material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';
import { useForm, register, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import FormProvider from 'hooks/form/form-provider';
import { useSnackbar } from 'components/snackbar';
import { CandidateJobPreferencesRule } from 'models/CandidateProfileSchema';
import TypeAhead from 'components/TypeAhead';
import { endpoints } from 'utils/axios';
import { addJobPreferance } from 'api/users';

const BpIcon = styled('span')(({ theme }) => ({
  borderRadius: 6,
  width: 20,
  height: 20,
  boxShadow: 'inset 0 0 0 1.5px rgba(16,22,26,.2), inset 0 -1px 0 rgba(16,22,26,.1)',
  backgroundImage: 'linear-gradient(180deg,hsla(0,0%,100%,.8),hsla(0,0%,100%,0))',
  '.Mui-focusVisible &': {
    outline: '2px auto rgba(19,124,189,.6)',
    outlineOffset: 2,
  },
  'input:hover ~ &': {
    backgroundColor: '#ebf1f5',
    ...theme.applyStyles('dark', {
      backgroundColor: '#30404d',
    }),
  },
  'input:disabled ~ &': {
    boxShadow: 'none',
    background: 'rgba(206,217,224,.5)',
    ...theme.applyStyles('dark', {
      background: 'rgba(57,75,89,.5)',
    }),
  },
  ...theme.applyStyles('dark', {
    boxShadow: '0 0 0 1px rgb(16 22 26 / 40%)',
    backgroundColor: '#394b59',
    backgroundImage: 'linear-gradient(180deg,hsla(0,0%,100%,.05),hsla(0,0%,100%,0))',
  }),
}));

const BpCheckedIcon = styled(BpIcon)({
  backgroundColor: '#137cbd',
  backgroundImage: 'linear-gradient(180deg,hsla(0,0%,100%,.1),hsla(0,0%,100%,0))',
  '&::before': {
    display: 'block',
    width: 20,
    height: 20,
    backgroundImage:
      "url(\"data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cpath" +
      " fill-rule='evenodd' clip-rule='evenodd' d='M12 5c-.28 0-.53.11-.71.29L7 9.59l-2.29-2.3a1.003 " +
      "1.003 0 00-1.42 1.42l3 3c.18.18.43.29.71.29s.53-.11.71-.29l5-5A1.003 1.003 0 0012 5z' fill='%23fff'/%3E%3C/svg%3E\")",
    content: '""',
  },
  'input:hover ~ &': {
    backgroundColor: '#106ba3',
  },
});


/**
 * A styled Checkbox component with custom icons and hover effects.
 * This component uses the BpIcon and BpCheckedIcon for rendering
 * the unchecked and checked states respectively.
 * 
 * @param {object} props - Properties to be passed to the Checkbox, 
 *                         including state and event handlers.
 * @returns {React.ReactElement} A Checkbox element with a custom style.
 */

function BpCheckbox(props) {
  return (
    <Checkbox
      sx={{ '&:hover': { bgcolor: 'transparent' } }}
      disableRipple
      color="default"
      checkedIcon={<BpCheckedIcon />}
      icon={<BpIcon />}
      inputProps={{ 'aria-label': 'Checkbox demo' }}
      {...props}
    />
  );
}


/**
 * JopApplicationDrawer component handles the rendering and submission 
 * of a job application form. It allows users to specify their job 
 * preferences, compensation details, notice period, and availability.
 * 
 * Props:
 * - onClose: Function to call when the drawer is closed.
 * - data: Initial data to populate the form with existing job preferences.
 * - onUpdatePreference: Function to call with updated job preferences 
 *   after successful submission.
 * 
 * The component uses React Hook Form for form management and validation, 
 * and displays various fields for users to input their job preferences.
 * It also includes error handling and displays notifications on form 
 * submission results.
 */

const JopApplicationDrawer = ({ onClose, data, onUpdatePreference }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [onSelect, setOnSelect] = useState(0);
  const [jobPreference, setJobPreference] = useState(['both']);
  const [inputValue, setInputValue] = useState('');

  const buyoutFieldRef = useRef(null);

  const workModelFormat = ['In office', 'Work from Home', 'Hybrid'];
  const preferredJobShifts = ['Daytime', 'Evening', 'Night'];
  const jobKind = [
    { label: "Either (Full Time as well as Part Time)", value: "both" },
    { label: "Full Time", value: "Full time" },
    { label: "Part Time/Temporary/Internship", value: "Part time" },
  ];

  const defaultValues = {
    notice_period: '',
    buyout_period: '',
    is_negotiable: false,
    total_ctc: null,
    compensation: '',
    fixed_ctc: null,
    variable_ctc: null,
    other_ctc: '',
    expected_ctc: '',
    work_format: ['In office'],
    job_type: ['both'],
    preferred_job_shifts: ['Daytime'],
    prefered_location: [],
    hour: null,
    hourly_rate: null
  };

  const methods = useForm({
    defaultValues,
    mode: 'onChange',
    resolver: yupResolver(CandidateJobPreferencesRule(0, jobPreference)),
  });

  const {
    control,
    register,
    handleSubmit,
    setValue,
    watch,
    getValues,
    setError,
    clearErrors,
    reset,
    formState: { touchedFields, errors, isDirty, isValid, dirtyFields, isSubmitting },
  } = methods;

  const watchedFixedCtc = watch('fixed_ctc');
  const watchedVariableCtc = watch('variable_ctc');

  const isNegotiable = watch('is_negotiable');

  useEffect(() => {
    const fixed = parseFloat(String(watchedFixedCtc).replace(/,/g, '')) || 0;
    const variable = parseFloat(String(watchedVariableCtc).replace(/,/g, '')) || 0;
    const total = fixed + variable;

    if (getValues('total_ctc') !== total) {
      setValue('total_ctc', total, { shouldDirty: true });
    }
  }, [watchedFixedCtc, watchedVariableCtc, setValue, getValues]);

  useEffect(() => {
    fetchjobs();
  }, []);

  useEffect(() => {
    if (onSelect > 5) setTimeout(() => setOnSelect(5), 3000)
  }, [onSelect]);

  useEffect(() => {
    if (data) {
      reset({ ...defaultValues, ...data });
      setOnSelect(data?.prefered_location?.length || 0);
      setJobPreference(data?.job_type || ['both']);
    }
  }, [data, reset]);

  const fetchjobs = async () => {
    try {
      if (data) {
        reset({ ...defaultValues, ...data });
        setOnSelect(data?.prefered_location?.length || 0);
        setJobPreference(data?.job_type || ['both']);
      }
    } catch (error) {
      console.error('Error fetching jobs:', error);
    }
  };

  const onSubmit = handleSubmit(async (formData) => {
    const response = await addJobPreferance(formData);
    if (response?.success === true && response?.data) {
      onUpdatePreference(response.data?.job_preferences);
      onClose();
    } else if (response?.success === false) {
      enqueueSnackbar(response.message, { variant: 'error' });
    }
  });

  /**
   * Handles changes to the selected values in the multi-select fields.
   * If the selection includes 'Any', it sets the value of the field to all
   * values. If the selection does not include 'Any' and the previous selection
   * did, it sets the value of the field to an empty array. Otherwise, it simply
   * sets the value of the field to the selected values.
   * @param {string} name - The name of the field.
   * @param {array} previousValue - The previous value of the field.
   * @param {array} value - The new value of the field.
   * @param {array} allValue - All the values of the field.
   */
  const handleChange = (name, previousValue, value, allValue) => {
    if (value?.includes('Any') && !previousValue?.includes('Any')) {
      setValue(name, allValue, { shouldDirty: true });
    } else if (previousValue?.includes('Any') && !value?.includes('Any')) {
      setValue(name, [], { shouldDirty: true });
    } else {
      setValue(name, value, { shouldDirty: true });
    }
  };

  /**
   * Handles changes to the selected job type. Sets the job preference to the
   * selected job type.
   * @param {string} selected - The selected job type.
   */
  const handleJobType = (selected) => {
    setJobPreference([selected]);
  };

  return (
    <Box sx={{ width: { xs: '100%', md: '450px' } }} className="job-app-drawer-container" role="presentation" >
      <Box className="drawer-header">
        <Stack direction="row" alignItems={'flex-start'} spacing={1}>
          <IconButton aria-label="delete" sx={{ paddingTop: 0, paddingBottom: 0 }} onClick={onClose}>
            <SvgIcon>
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M6.22796 5.04477C5.90134 4.71815 5.37178 4.71815 5.04516 5.04477C4.71854 5.37139 4.71854 5.90095 5.04516 6.22757L10.8174 11.9998L5.04516 17.772C4.71854 18.0987 4.71854 18.6282 5.04516 18.9548C5.37178 19.2815 5.90134 19.2815 6.22796 18.9548L12.0002 13.1826L17.7724 18.9548C18.0991 19.2815 18.6286 19.2815 18.9552 18.9548C19.2819 18.6282 19.2819 18.0987 18.9552 17.772L13.183 11.9998L18.9552 6.22757C19.2819 5.90095 19.2819 5.37139 18.9552 5.04477C18.6286 4.71815 18.0991 4.71815 17.7724 5.04477L12.0002 10.817L6.22796 5.04477Z" fill="white" />
              </svg>
            </SvgIcon>
          </IconButton>
          <Box>
            <Typography className="title">Job Application</Typography>
            <Typography className="sub-text">Please help us with answers to few questions to complete your application</Typography>
          </Box>
        </Stack>
      </Box>

      <FormProvider methods={methods} onSubmit={onSubmit}>
        <Box className="drawer-content">
          <Box className="card-container" mb={2}>
            <Box className="card-title">
              <Typography className="title-text">General Job Preferences</Typography>
            </Box>
            <Box className="card-body">
              <Typography className="step-heading" variant="h6" sx={{ marginBottom: '16px!important' }}>What kind of job you are looking for?</Typography>
              <Box mb={3} mt={2}>
                {/* job type */}
                <FormControl sx={{ width: '100%' }}>
                  <Controller
                    control={control}
                    name="job_type"
                    render={({ field }) => (
                      <RadioGroup
                        row
                        value={field.value?.[0] || ''}
                        onChange={(e) => {
                          const selected = e.target.value;
                          field.onChange([selected]);
                          handleJobType(selected);
                        }}
                      >
                        {jobKind?.map((job) => (
                          <FormControlLabel
                            key={job?.value}
                            value={job?.value}
                            control={<Radio />}
                            label={job?.label}
                          />
                        ))}
                      </RadioGroup>
                    )}
                  />
                </FormControl>
              </Box>
              <Box>
                <Stack>
                  {/* work format */}
                  <FormControl fullWidth size="small" sx={{ marginBottom: '24px' }}>
                    <Controller
                      control={control}
                      name="work_format"
                      render={({ field }) => (
                        <Autocomplete
                          {...field}
                          multiple
                          size="small"
                          options={workModelFormat}
                          getOptionLabel={(option) => option}
                          disableCloseOnSelect={true}
                          onChange={(event, value) => {
                            field.onChange(value);
                          }}
                          renderOption={(props, option, { selected }) => (
                            <li {...props}>
                              <Checkbox
                                icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                                checkedIcon={<CheckBoxIcon fontSize="small" />}
                                checked={selected}
                              />
                              {option}
                            </li>
                          )}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              variant="outlined"
                              label="Preferred Work Format"
                              error={!!errors.work_format}
                              helperText={errors.work_format?.message}
                            />
                          )}
                        />
                      )}
                    />
                    <FormHelperText>Select one or more options</FormHelperText>
                  </FormControl>
                  {/* location */}
                  <FormControl fullWidth size="small">
                    <Controller
                      control={control}
                      name="prefered_location"
                      defaultValue={defaultValues?.prefered_location || []}
                      render={({ field }) => (
                        <TypeAhead
                          {...field}
                          {...register('prefered_location')}
                          label="Preferred Locations (select upto 5)"
                          multiple
                          selectAsCheckbox
                          isCloseHide
                          disableCloseOnSelect
                          api={`${process.env.REACT_APP_END_POINT}${endpoints?.services?.cluster}`}
                          size="small"
                          displayTextKey="name"
                          maxSelection={5}
                          inputValue={inputValue}
                          onInputChange={(e, newInputValue, reason) => {
                            setInputValue(newInputValue);
                          }}
                          onBlur={() => {
                            setInputValue('');
                          }}
                          onChange={(e, dataObjs, values) => {
                            if (values?.length <= 5) {
                              setOnSelect(values?.length);
                              setInputValue('');
                            } else {
                              setOnSelect(onSelect + 1);
                            }
                          }}
                        />
                      )}
                    />
                    {(onSelect && onSelect > 5) ? <FormHelperText error>You can select up to 5 locations only</FormHelperText> : null}
                    <FormHelperText>Select one or more options</FormHelperText>
                  </FormControl>
                  {/* job shift(working hours) */}
                  <FormControl fullWidth sx={{ paddingTop: '20px' }}>
                    <Controller
                      control={control}
                      name="preferred_job_shifts"
                      render={({ field }) => (
                        <Autocomplete
                          {...field}
                          multiple
                          size="small"
                          options={preferredJobShifts}
                          getOptionLabel={(option) => option}
                          disableCloseOnSelect={true}
                          onChange={(event, value) => {
                            handleChange(field?.name, field?.value, value, preferredJobShifts);
                          }}
                          renderOption={(props, option, { selected }) => (
                            <li {...props}>
                              <Checkbox
                                icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                                checkedIcon={<CheckBoxIcon fontSize="small" />}
                                checked={selected}
                              />
                              {option}
                            </li>
                          )}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Preferred Working Hours"
                              variant="outlined"
                              error={!!errors.preferred_job_shifts}
                              helperText={errors.preferred_job_shifts?.message}
                            />
                          )}
                        />
                      )}
                    /><FormHelperText>Select one or more options</FormHelperText>
                  </FormControl>
                </Stack>
              </Box>
            </Box>
          </Box>
          {/* full time */}
          {(jobPreference?.includes('Full time') || jobPreference?.includes('both')) &&
            <Box mb={2.5}>
              <Box className="card-container">
                <Box className="card-title">
                  <Typography className="title-text">Full Time Job Details</Typography>
                </Box>
                <Box className="card-body">
                  <Typography className="step-heading" variant="h6" sx={{ marginBottom: '16px!important' }}>Current Annual Compensation</Typography>
                  <Box className="annual-compensation" mb={3} mt={2}>
                    <Box>
                      <Stack>
                        {/* fixed ctc */}
                        <FormControl fullWidth error={!!errors?.fixed_ctc} sx={{ paddingTop: '10px' }}>
                          <Controller
                            control={control}
                            name="fixed_ctc"
                            defaultValue={defaultValues?.fixed_ctc || ''}
                            render={({ field }) => {
                              const [isEditing, setIsEditing] = React.useState(false);

                              return (
                                <TextField
                                  {...field}
                                  id="fixed_ctc"
                                  name="fixed_ctc"
                                  label="Fixed Component"
                                  variant="outlined"
                                  size="small"
                                  error={!!errors.fixed_ctc}
                                  type="text"
                                  value={
                                    isEditing
                                      ? field.value
                                      : field.value
                                        ? new Intl.NumberFormat('en-IN').format(field.value)
                                        : ''
                                  }
                                  onFocus={() => setIsEditing(true)}
                                  onBlur={(e) => {
                                    setIsEditing(false);

                                    let rawValue = e.target.value.replace(/,/g, '');
                                    let numValue = parseInt(rawValue, 10);

                                    if (!isNaN(numValue)) {
                                      field.onChange(numValue);
                                      const variableValue = parseInt(getValues('variable_ctc'), 10) || 0;
                                      const total = numValue + variableValue;
                                      setValue('total_ctc', total, { shouldDirty: true });
                                    }
                                  }}
                                  onChange={(e) => {
                                    let rawValue = e.target.value.replace(/,/g, '');
                                    if (/^\d{0,9}$/.test(rawValue)) {
                                      field.onChange(rawValue);
                                    }
                                  }}
                                  inputProps={{
                                    inputMode: 'numeric',
                                    pattern: '[0-9]*',
                                    maxLength: 9
                                  }}
                                  InputProps={{
                                    startAdornment: (
                                      field?.value ? (
                                        <InputAdornment position="start">
                                          <CurrencyRupeeIcon fontSize="small" />
                                        </InputAdornment>) : null
                                    ),
                                  }}
                                />
                              );
                            }}
                          />
                          {errors?.fixed_ctc && <FormHelperText error>{errors.fixed_ctc.message}</FormHelperText>}
                        </FormControl>
                        {/* variable ctc */}
                        <FormControl fullWidth error={!!errors?.variable_ctc} sx={{ paddingTop: '20px' }}>
                          <Controller
                            control={control}
                            name="variable_ctc"
                            defaultValue={defaultValues?.variable_ctc || ''}
                            render={({ field }) => {
                              const [isEditing, setIsEditing] = React.useState(false);

                              return (
                                <TextField
                                  {...field}
                                  id="variable_ctc"
                                  name="variable_ctc"
                                  label="Variable Component"
                                  variant="outlined"
                                  size="small"
                                  error={!!errors.variable_ctc}
                                  helperText={errors.variable_ctc?.message}
                                  type="text"
                                  value={
                                    isEditing
                                      ? field.value
                                      : field.value
                                        ? new Intl.NumberFormat('en-IN').format(field.value)
                                        : ''
                                  }
                                  onFocus={() => setIsEditing(true)}
                                  onBlur={(e) => {
                                    setIsEditing(false);

                                    let rawValue = e.target.value.replace(/,/g, '');
                                    let numValue = parseInt(rawValue, 10);
                                    const valueToSet = !isNaN(numValue) ? numValue : null;
                                    field.onChange(valueToSet);
                                  }}
                                  onChange={(e) => {
                                    let rawValue = e.target.value.replace(/,/g, '');
                                    if (/^\d{0,9}$/.test(rawValue)) {
                                      field.onChange(rawValue);
                                    }
                                  }}
                                  inputProps={{
                                    inputMode: 'numeric',
                                    pattern: '[0-9]*',
                                    maxLength: 9
                                  }}
                                  InputProps={{
                                    startAdornment: (
                                      field?.value ? (
                                        <InputAdornment position="start">
                                          <CurrencyRupeeIcon fontSize="small" />
                                        </InputAdornment>) : null
                                    ),
                                  }}
                                />
                              );
                            }}
                          />
                          {/* {errors?.variable_ctc && <FormHelperText error>{errors.variable_ctc.message}</FormHelperText>} */}
                        </FormControl>
                        {/* other ctc */}
                        <FormControl fullWidth sx={{ paddingTop: '20px', paddingBottom: '20px' }}>
                          <Controller
                            control={control}
                            name="other_ctc"
                            defaultValue={defaultValues?.other_ctc || ''}
                            rules={{
                              validate: (value) => {
                                const wordCount = value.trim().split(/\s+/).length;
                                return wordCount <= 25 || "Maximum 25 words allowed.";
                              }
                            }}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                id="other_ctc"
                                name="other_ctc"
                                label="Other Perks"
                                variant="outlined"
                                size="small"
                                defaultValue={defaultValues?.other_ctc || ''}
                                error={!!errors.other_ctc}
                                helperText={errors.other_ctc?.message}
                                onChange={(e) => {
                                  const inputValue = e.target.value;
                                  const words = inputValue.trim().split(/\s+/);
                                  if (words.length <= 25) {
                                    field.onChange(inputValue);
                                    clearErrors('other_ctc');
                                  } else {
                                    setError('other_ctc', { message: "Maximum 25 words allowed." });
                                  }
                                }}
                              />
                            )}
                          />
                        </FormControl>
                        {/* total ctc */}
                        <Box className="compensation-det-block">
                          {watch('total_ctc') && watch('total_ctc') > '0' ?
                            <Box className="after-comp-amt" >
                              <Typography className="comp-text">Your Total Annual Compensation</Typography>
                              <Typography className="comp-amt" mt={2}>
                                ₹{watch('total_ctc') && `${new Intl.NumberFormat('en-IN').format(watch('total_ctc'))}`}
                              </Typography>
                              <Typography className="comp-text">{watch('other_ctc') && `Along with ${watch('other_ctc')}`}</Typography>
                            </Box> :
                            <Box className="before-comp-amt">
                              <Typography className="comp-text">Please enter your compensation details on the left</Typography>
                            </Box>}
                        </Box>
                      </Stack>
                    </Box>
                  </Box>
                  <Divider sx={{ borderStyle: 'dashed', borderWidth: '1px', marginBottom: '1rem' }} />
                  <Typography className="step-heading" variant="h6" sx={{ marginBottom: '16px!important', paddingBottom: '1rem' }}>Expected Annual Compensation</Typography>
                  <Stack>
                    {/* expected ctc */}
                    <FormControl fullWidth size="small" error={!!errors?.expected_ctc}>
                      <Controller
                        control={control}
                        name="expected_ctc"
                        defaultValue={defaultValues?.expected_ctc || ''}
                        render={({ field }) => {
                          const [isEditing, setIsEditing] = React.useState(false);

                          return (
                            <TextField
                              {...field}
                              id="expected_ctc"
                              name="expected_ctc"
                              label="Expected Annual Compensation (Optional)"
                              variant="outlined"
                              size="small"
                              error={!!errors.expected_ctc}
                              helperText={errors.expected_ctc?.message}
                              value={
                                isEditing
                                  ? field.value
                                  : field.value
                                    ? new Intl.NumberFormat('en-IN').format(field.value)
                                    : ''
                              }
                              onFocus={() => setIsEditing(true)}
                              onBlur={(e) => {
                                setIsEditing(false);
                                let rawValue = e.target.value.replace(/,/g, '');
                                let numValue = parseInt(rawValue, 10);
                                const valueToSet = !isNaN(numValue) ? numValue : null;
                                field.onChange(valueToSet);
                              }}
                              onChange={(e) => {
                                let rawValue = e.target.value.replace(/,/g, '');
                                if (/^\d{0,9}$/.test(rawValue)) {
                                  field.onChange(rawValue);
                                }
                              }}
                              inputProps={{
                                inputMode: 'numeric',
                                pattern: '[0-9]*',
                                maxLength: 9
                              }}
                              InputProps={{
                                startAdornment: (
                                  field?.value ? (
                                    <InputAdornment position="start">
                                      <CurrencyRupeeIcon fontSize="small" />
                                    </InputAdornment>) : null
                                ),
                              }}
                            />
                          );
                        }}
                      />
                      <FormHelperText error={false}>Including fixed & variable components</FormHelperText>
                    </FormControl>
                  </Stack>
                  <Divider sx={{ borderStyle: 'dashed', borderWidth: '1px', marginBottom: '1rem' }} />

                  <Typography className="step-heading" variant="h6" sx={{ marginBottom: '16px!important' }}>Notice Period</Typography>
                  <Box className="current-job-det" mt={2}>
                    <Stack>
                      {/* notice period */}
                      <FormControl fullWidth size="small" error={!!errors.notice_period}>
                        <InputLabel id="notice_period">Notice Period</InputLabel>
                        <Controller
                          control={control}
                          name="notice_period"
                          defaultValue={defaultValues?.notice_period || ''}
                          render={({ field }) => (
                            <Select
                              {...field}
                              {...register('notice_period')}
                              labelId="notice_period"
                              label="Notice Period"
                              variant="outlined"
                              fullWidth
                              onChange={(e) => {
                                field.onChange(e.target.value);
                              }}
                            >
                              <MenuItem value="0 days (immediate)">0 days (immediate)</MenuItem>
                              <MenuItem value="15">15 Days</MenuItem>
                              <MenuItem value="30">30 Days</MenuItem>
                              <MenuItem value="45">45 Days</MenuItem>
                              <MenuItem value="60">60 Days</MenuItem>
                              <MenuItem value="75">75 Days</MenuItem>
                              <MenuItem value="90">90 Days</MenuItem>
                              <MenuItem value="90+">90+ Days</MenuItem>
                            </Select>
                          )}
                        />
                        {errors.notice_period && <FormHelperText error>{errors.notice_period?.message}</FormHelperText>}
                      </FormControl>
                      {/* is negotiable */}
                      <FormControl error={!!errors.is_negotiable} className="neogtiable-chk" size="small">
                        <Controller
                          name="is_negotiable"
                          control={control}
                          render={({ field }) => (
                            <FormControlLabel
                              control={
                                <BpCheckbox
                                  {...field}
                                  id="is_negotiable"
                                  checked={!!field.value}
                                  onChange={(event) => {
                                    const isChecked = event.target.checked;
                                    field.onChange(isChecked);
                                    if (!isChecked) {
                                      setValue('buyout_period', '', { shouldDirty: true });
                                    }
                                    if (isChecked) {
                                      setTimeout(() => {
                                        buyoutFieldRef.current?.focus();
                                      }, 0);
                                    }
                                  }}
                                />
                              }
                              label="Notice period is negotiable"
                              sx={{ paddingLeft: 0.5 }}
                            />
                          )}
                        />
                        {errors.is_negotiable && (
                          <Typography variant="caption" color="error">
                            {errors.is_negotiable.message}
                          </Typography>
                        )}
                      </FormControl>
                      {/* buyout period */}
                      <FormControl fullWidth sx={{ paddingTop: '20px' }}>
                        <Controller
                          control={control}
                          name="buyout_period"
                          defaultValue={defaultValues?.buyout_period || ''}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              inputRef={buyoutFieldRef}
                              id="buyout_period"
                              label="If notice period is negotiable, please state how much (Optional)"
                              disabled={!isNegotiable}
                              variant="outlined"
                              size="small"
                              onChange={(e) => {
                                field.onChange(e.target.value);
                              }}
                              error={!!errors.buyout_period}
                              helperText={errors.buyout_period?.message}
                            />
                          )}
                        />
                      </FormControl>
                    </Stack>
                  </Box>
                </Box>
              </Box>
            </Box >}
          {/* part time */}
          {(jobPreference?.includes('Part time') || jobPreference?.includes('both')) &&
            <Box mb={2.5}>
              <Box className="card-container">
                <Box className="card-title">
                  <Typography className="title-text">Part Time Job Details</Typography>
                </Box>
                <Box className="card-body">
                  <Box>
                    <Stack>
                      {/* hourly rate */}
                      <FormControl fullWidth error={!!errors?.hourly_rate}>
                        <Controller
                          control={control}
                          name="hourly_rate"
                          defaultValue={defaultValues?.hourly_rate || ''}
                          render={({ field }) => {
                            const [isEditing, setIsEditing] = React.useState(false);

                            return (
                              <TextField
                                {...field}
                                id="hourly_rate"
                                name="hourly_rate"
                                label="Hourly Rate (per hour)"
                                variant="outlined"
                                size="small"
                                error={!!errors.hourly_rate}
                                helperText={errors.hourly_rate?.message}
                                type="text"
                                value={
                                  isEditing
                                    ? field.value
                                    : field.value
                                      ? new Intl.NumberFormat('en-IN').format(field.value)
                                      : ''
                                }
                                onFocus={() => setIsEditing(true)}
                                onBlur={(e) => {
                                  setIsEditing(false);
                                  let rawValue = e.target.value.replace(/,/g, '');
                                  let numValue = parseInt(rawValue, 10);
                                  const valueToSet = !isNaN(numValue) ? numValue : null;
                                  field.onChange(valueToSet);
                                }}
                                onChange={(e) => {
                                  let rawValue = e.target.value.replace(/,/g, '');
                                  if (/^\d{0,6}$/.test(rawValue)) {
                                    field.onChange(rawValue);
                                  }
                                }}
                                inputProps={{
                                  inputMode: 'numeric',
                                  pattern: '[0-9]*',
                                  maxLength: 6
                                }}
                                InputProps={{
                                  startAdornment: field?.value ? (
                                    <InputAdornment position="start">
                                      <CurrencyRupeeIcon fontSize="small" />
                                    </InputAdornment>
                                  ) : null
                                }}
                              />
                            );
                          }}
                        />
                      </FormControl>
                      {/* hour */}
                      <FormControl fullWidth sx={{ paddingTop: '20px' }}>
                        <Controller
                          control={control}
                          name="hour"
                          defaultValue={defaultValues?.hour || ''}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              id="hour"
                              name="hour"
                              label="Your Availability (hours per week)"
                              variant="outlined"
                              size="small"
                              value={field.value || ''}
                              type="text"
                              error={!!errors.hour}
                              helperText={errors.hour?.message}
                              onChange={(e) => {
                                const inputValue = e.target.value;
                                if (/^\d{0,2}$/.test(inputValue)) {
                                  field.onChange(inputValue);
                                }
                              }}
                              inputProps={{
                                inputMode: 'numeric',
                                pattern: '[0-9]*',
                                maxLength: 2
                              }}
                            />
                          )}
                        />
                      </FormControl>
                    </Stack>
                  </Box>
                </Box>
              </Box>
            </Box>
          }
        </Box>

        <Box className="drawer-footer">
          <Stack direction="row" spacing={2}>
            <Button fullWidth size="large" className="cancel-btn" onClick={onClose}>
              Cancel
            </Button>
            <Button fullWidth variant="contained" size="large" className="update-btn" onClick={onSubmit} disabled={!isDirty || !isValid || isSubmitting}>
              {isSubmitting ? 'Updating...' : 'Update'}
            </Button>
          </Stack>
        </Box>
      </FormProvider>
    </Box>
  );
};

export default JopApplicationDrawer;
